import React, { useMemo } from 'react';
import { colors } from './TabNavigation';

// ── Constants ────────────────────────────────────────────────────────────────

const SIGN_GLYPHS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const ROMAN = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'];

// Planets to display on the wheel
const WHEEL_PLANETS = [
  'SUN','MOON','MERCURY','VENUS','MARS',
  'JUPITER','SATURN','URANUS','NEPTUNE','PLUTO',
  'CHIRON','NORTH_NODE',
];

// Only draw aspect lines between the main 10 planets
const MAIN_10 = new Set([
  'SUN','MOON','MERCURY','VENUS','MARS',
  'JUPITER','SATURN','URANUS','NEPTUNE','PLUTO',
]);

const ASPECT_STYLE = {
  conjunction: { color: '#c9956e', baseOpacity: 0.75, width: 1.2 },
  opposition:  { color: '#c97070', baseOpacity: 0.70, width: 1.1 },
  square:      { color: '#c97070', baseOpacity: 0.60, width: 0.9 },
  trine:       { color: '#6b9bc9', baseOpacity: 0.70, width: 1.0 },
  sextile:     { color: '#6b9bc9', baseOpacity: 0.55, width: 0.8 },
};

// ── Coordinate helpers ────────────────────────────────────────────────────────

/**
 * Ecliptic longitude → SVG angle (radians).
 * ASC maps to 9 o'clock (π). Zodiac runs counter-clockwise on screen.
 * IC → bottom, DSC → right, MC → top.
 */
function lonToAngle(lon, ascLon) {
  return ((180 - (lon - ascLon) + 720) % 360) * (Math.PI / 180);
}

function pt(cx, cy, angle, r) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

/**
 * SVG path for one zodiac sign band (sector between two concentric circles).
 * Uses counter-clockwise outer arc (sweep=0) and clockwise inner arc (sweep=1).
 * Each sector is always 30° — large-arc flag is always 0.
 */
function signBandPath(cx, cy, signIndex, ascLon, rOuter, rInner) {
  const a1 = lonToAngle(signIndex * 30, ascLon);
  const a2 = lonToAngle((signIndex + 1) * 30, ascLon);
  const o1 = pt(cx, cy, a1, rOuter);
  const o2 = pt(cx, cy, a2, rOuter);
  const i1 = pt(cx, cy, a1, rInner);
  const i2 = pt(cx, cy, a2, rInner);
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${rOuter} ${rOuter} 0 0 0 ${o2.x} ${o2.y}`,
    `L ${i2.x} ${i2.y}`,
    `A ${rInner} ${rInner} 0 0 1 ${i1.x} ${i1.y}`,
    'Z',
  ].join(' ');
}

// ── Main component ────────────────────────────────────────────────────────────

function ChartWheel({ chart, size = 340 }) {
  const cx = size / 2;
  const cy = size / 2;

  // Radii (proportional so the wheel scales with `size`)
  const RO = size * 0.456;  // outer zodiac ring edge   ≈ 155 @ 340px
  const RZ = size * 0.385;  // zodiac / house boundary   ≈ 131
  const RH = size * 0.312;  // inner house ring           ≈ 106
  const RP = size * 0.265;  // planet glyph (base)        ≈  90
  const RA = size * 0.182;  // aspect circle / chord ends ≈  62

  const ascLon = chart?.houses?.ascendant?.longitude ?? 0;
  const mcLon  = chart?.houses?.mc?.longitude ?? 0;
  const icLon  = (mcLon + 180) % 360;
  const dscLon = (ascLon + 180) % 360;

  // Shorthand angle helpers bound to this chart's ASC
  const toAngle = (lon) => lonToAngle(lon, ascLon);
  const p = (angle, r) => pt(cx, cy, angle, r);

  // ── Derived data (memoised) ─────────────────────────────────────────────

  const planetEntries = useMemo(() => {
    if (!chart) return [];
    const entries = WHEEL_PLANETS.map((key) => {
      const planet = chart.planets[key];
      if (!planet) return null;
      return {
        key,
        symbol: planet.symbol || '?',
        longitude: planet.longitude,
        retro: (planet.longitudeSpeed || 0) < 0,
        angle: lonToAngle(planet.longitude, ascLon),
      };
    }).filter(Boolean);

    // Sort by angle for adjacency-based collision detection
    entries.sort((a, b) => a.angle - b.angle);

    // Simple deconfliction: alternate radii for planets within 14° of each other
    const MIN_GAP = 14 * (Math.PI / 180);
    entries.forEach((entry, i) => {
      if (i === 0) { entry.r = RP; return; }
      const prev = entries[i - 1];
      let diff = Math.abs(entry.angle - prev.angle);
      if (diff > Math.PI) diff = 2 * Math.PI - diff;
      entry.r = diff < MIN_GAP ? (prev.r === RP ? RP - 14 : RP) : RP;
    });

    return entries;
  }, [chart.planets, ascLon, RP]); // eslint-disable-line react-hooks/exhaustive-deps

  const aspectLines = useMemo(() => {
    if (!chart) return [];
    return chart.aspects
      .filter((a) =>
        MAIN_10.has(a.planet1.key) &&
        MAIN_10.has(a.planet2.key) &&
        parseFloat(a.orb) < 8
      )
      .map((a) => {
        const lon1 = chart.planets[a.planet1.key]?.longitude;
        const lon2 = chart.planets[a.planet2.key]?.longitude;
        if (lon1 == null || lon2 == null) return null;
        const style = ASPECT_STYLE[(a.aspect.name || '').toLowerCase()];
        if (!style) return null;
        // Tighten opacity for close orbs (orb 0 → full, orb 8 → 40%)
        const orb = parseFloat(a.orb);
        const opacity = style.baseOpacity * Math.max(0.4, 1 - orb / 12);
        return {
          key: `${a.planet1.key}-${a.planet2.key}`,
          ang1: lonToAngle(lon1, ascLon),
          ang2: lonToAngle(lon2, ascLon),
          color: style.color,
          opacity,
          width: style.width,
        };
      })
      .filter(Boolean);
  }, [chart.aspects, chart.planets, ascLon]); // eslint-disable-line react-hooks/exhaustive-deps

  const houseCusps = chart?.houses?.houses || [];

  // ── Render ──────────────────────────────────────────────────────────────

  if (!chart) return null;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '56px',
      padding: '0 20px 56px',
      boxSizing: 'border-box',
      borderBottom: `1px solid ${colors.borderSubtle}`,
    }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block', width: '100%', height: 'auto', maxWidth: `${size}px`, overflow: 'visible' }}
      >
        {/* ── Background fill ── */}
        <circle cx={cx} cy={cy} r={RO} fill={colors.creamBg} />

        {/* ── Zodiac sign bands ── */}
        {SIGN_GLYPHS.map((_, i) => (
          <path
            key={`band-${i}`}
            d={signBandPath(cx, cy, i, ascLon, RO, RZ)}
            fill={i % 2 === 0
              ? 'rgba(201,149,110,0.07)'
              : 'rgba(201,149,110,0.02)'}
            stroke="none"
          />
        ))}

        {/* ── Outer ring border ── */}
        <circle cx={cx} cy={cy} r={RO}
          fill="none" stroke={colors.borderGold} strokeWidth="0.8" />

        {/* ── Zodiac/house boundary ── */}
        <circle cx={cx} cy={cy} r={RZ}
          fill="none" stroke={colors.borderGold} strokeWidth="0.8" opacity="0.9" />

        {/* ── Sign divider lines (every 30°) ── */}
        {SIGN_GLYPHS.map((_, i) => {
          const a = toAngle(i * 30);
          const p1 = p(a, RZ), p2 = p(a, RO);
          return (
            <line key={`sdiv-${i}`}
              x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke={colors.borderGold} strokeWidth="0.8" opacity="0.8"
            />
          );
        })}

        {/* ── Sign glyphs ── */}
        {SIGN_GLYPHS.map((sym, i) => {
          const a = toAngle(i * 30 + 15);
          const pos = p(a, (RO + RZ) / 2);
          return (
            <text key={`sglyph-${i}`}
              x={pos.x} y={pos.y}
              textAnchor="middle" dominantBaseline="central"
              fontSize="11" fill={colors.gold} opacity="0.8"
              fontFamily="'Segoe UI Symbol', 'Apple Symbols', 'Symbola', sans-serif"
            >
              {sym}
            </text>
          );
        })}

        {/* ── House cusp lines ── */}
        {houseCusps.map((house, i) => {
          const isAxis = i === 0 || i === 3 || i === 6 || i === 9;
          const a = toAngle(house.longitude);
          const p1 = p(a, isAxis ? RZ : RH);
          const p2 = p(a, RA);
          return (
            <line key={`hcusp-${i}`}
              x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke={isAxis ? colors.gold : colors.textMuted}
              strokeWidth={isAxis ? 1.0 : 0.6}
              opacity={isAxis ? 0.85 : 0.5}
            />
          );
        })}

        {/* ── Inner house ring ── */}
        <circle cx={cx} cy={cy} r={RH}
          fill="none" stroke={colors.borderGold} strokeWidth="0.7" opacity="0.8" />

        {/* ── Aspect circle ── */}
        <circle cx={cx} cy={cy} r={RA}
          fill="none" stroke={colors.borderGold} strokeWidth="0.6" opacity="0.7" />

        {/* ── House numbers ── */}
        {houseCusps.map((house, i) => {
          const next = houseCusps[(i + 1) % 12];
          let lon1 = house.longitude;
          let lon2 = next.longitude;
          if (lon2 < lon1) lon2 += 360;
          const midLon = (lon1 + lon2) / 2;
          const a = toAngle(midLon);
          const pos = p(a, (RH + RZ) / 2);
          return (
            <text key={`hnum-${i}`}
              x={pos.x} y={pos.y}
              textAnchor="middle" dominantBaseline="central"
              fontSize="7.5" fill={colors.textMuted} opacity="0.7"
            >
              {ROMAN[i]}
            </text>
          );
        })}

        {/* ── Angle labels (AC / DC / MC / IC) ── */}
        {[
          { lon: ascLon, label: 'AC' },
          { lon: dscLon, label: 'DC' },
          { lon: mcLon,  label: 'MC' },
          { lon: icLon,  label: 'IC' },
        ].map(({ lon, label }) => {
          const a = toAngle(lon);
          const pos = p(a, RZ + 12);
          return (
            <text key={label}
              x={pos.x} y={pos.y}
              textAnchor="middle" dominantBaseline="central"
              fontSize="8" fill={colors.gold}
              fontWeight="500" letterSpacing="0.5" opacity="0.9"
            >
              {label}
            </text>
          );
        })}

        {/* ── Aspect lines (chords inside RA circle) ── */}
        {aspectLines.map((asp) => {
          const p1 = p(asp.ang1, RA);
          const p2 = p(asp.ang2, RA);
          return (
            <line key={asp.key}
              x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
              stroke={asp.color} strokeWidth={asp.width} opacity={asp.opacity}
            />
          );
        })}

        {/* ── Centre ornament ── */}
        <circle cx={cx} cy={cy} r="7"
          fill="none" stroke={colors.borderGold} strokeWidth="0.5" opacity="0.5" />
        <circle cx={cx} cy={cy} r="2" fill={colors.gold} opacity="0.35" />

        {/* ── Planet glyphs ── */}
        {planetEntries.map(({ key, symbol, angle, r, retro }) => {
          const glyphPos = p(angle, r);
          // Small tick from house ring inward toward planet
          const tickOuter = p(angle, RH);
          const tickInner = p(angle, RH - 7);
          return (
            <g key={key}>
              <line
                x1={tickOuter.x} y1={tickOuter.y}
                x2={tickInner.x} y2={tickInner.y}
                stroke={colors.borderGold} strokeWidth="0.5" opacity="0.5"
              />
              <text
                x={glyphPos.x} y={glyphPos.y}
                textAnchor="middle" dominantBaseline="central"
                fontSize="11"
                fill={retro ? colors.twilightPurple : colors.textPrimary}
                fontFamily="inherit"
              >
                {symbol}
              </text>
              {retro && (
                <text
                  x={glyphPos.x + 7} y={glyphPos.y - 6}
                  fontSize="6.5" fill={colors.twilightPurple} opacity="0.75"
                >
                  ℞
                </text>
              )}
            </g>
          );
        })}

      </svg>
    </div>
  );
}

export default ChartWheel;
